import { useCallback, useMemo, useState } from 'react';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { format } from 'date-fns';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TableContainer from '../../../components/Table/TableContainer';
import TableHeading from '../../../components/Table/TableHeading';
import GenericTableCell from '../../../components/GenericTableCell';
import LoadingRows from '../../../components/Table/LoadingRows';
import NoDataCell from '../../../components/NoDataCell';
import HistoryRowDialog from './HistoryRowDialog';
import { useInfiniteCustomerHistory } from '../../../hooks/subscription';
import { customerIdSelector } from '../../../selectors/customer';
import CustomerIdService from '../../../services/customer-id.service';
import useQueryData from '../../../queries/useQueryData';
import { CUSTOMER_QUERY_KEY } from '../../../constants/query';
import { formatUtcDate } from '../../../utils/formatTime';
import { getSubscriptionTypeLabel } from '../../../utils/enums/subscriptionType';
import { getSubscriptionTermLabel } from '../../../utils/enums/subscriptionTerm';

// No percentage widths here - columns other than the two truncated ones should size to their
// content and let the table scroll horizontally rather than wrap or get squeezed.
const headings = [
  { title: 'Company Name', name: 'companyName', maxWidth: 180 },
  { title: 'Subscription Type', name: 'subscriptionTypeLabel' },
  { title: 'Subscription Term', name: 'subscriptionTermLabel' },
  { title: 'Support Tier', name: 'supportTier' },
  { title: 'Licensed Devices', name: 'licensedDeviceCount' },
  { title: 'Start Date', name: 'subscriptionStartDateLabel' },
  { title: 'End Date', name: 'subscriptionEndDateLabel' },
  { title: 'Changed By', name: 'changedByUserEmail', maxWidth: 200 },
  { title: 'Date', name: 'historyDateLabel' },
];

// Only these reflect the company/subscription's actual state, so only these are worth diffing -
// "Changed By" and "Date" describe the change itself and always differ row to row.
const DIFFABLE_FIELDS = [
  { column: 'companyName', raw: 'companyName' },
  { column: 'subscriptionTypeLabel', raw: 'subscriptionType' },
  { column: 'subscriptionTermLabel', raw: 'subscriptionTerm' },
  { column: 'supportTier', raw: 'supportTier' },
  { column: 'licensedDeviceCount', raw: 'licensedDeviceCount' },
  { column: 'subscriptionStartDateLabel', raw: 'subscriptionStartDate' },
  { column: 'subscriptionEndDateLabel', raw: 'subscriptionEndDate' },
];
const DIFFABLE_COLUMN_NAMES = new Set(DIFFABLE_FIELDS.map(({ column }) => column));
const TRUNCATED_COLUMN_NAMES = new Set(['companyName', 'changedByUserEmail']);

const normalize = value => value ?? null;

const formatDateLabel = date => formatUtcDate(date, 'yyyy-MM-dd', '--');

const getCellStyles = ({ name, maxWidth }, row) => ({
  whiteSpace: 'nowrap',
  ...(TRUNCATED_COLUMN_NAMES.has(name)
    ? { maxWidth, overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }
    : {}),
  ...(DIFFABLE_COLUMN_NAMES.has(name) && row.changedFields?.has(name)
    ? {
        backgroundColor: 'rgba(125, 255, 146, 0.16)',
        borderRadius: '4px',
        padding: '2px 6px',
        fontWeight: 600,
      }
    : {}),
});

const CompanyHistory = () => {
  // useQueryData is a one-off cache read that never re-renders on update, so on a cold cache it
  // would hand back null and leave the history query permanently disabled. The selected id is
  // written to storage before we can navigate here, so that is the dependable fallback.
  const customerQueryId = useQueryData(CUSTOMER_QUERY_KEY, customerIdSelector);
  const customerId = customerQueryId || CustomerIdService.getId();
  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } =
    useInfiniteCustomerHistory({ customerId });
  const [selectedRow, setSelectedRow] = useState(null);

  const loadMore = useCallback(() => {
    if (hasNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, fetchNextPage]);

  const [sentryRef] = useInfiniteScroll({
    loading: isFetchingNextPage,
    hasNextPage,
    onLoadMore: loadMore,
    rootMargin: '0px 0px 400px 0px',
  });

  const rows = useMemo(() => {
    const historyEntries = data?.pages?.flatMap(page => page.history || []) || [];

    // historyEntries is newest-first, so index + 1 is the chronologically previous entry. The
    // oldest loaded entry has none to compare against, so it is treated as a starting point and
    // highlighted in full - which is accurate once scrolling has pulled in the whole log.
    return historyEntries.map((entry, index) => {
      const previousEntry = historyEntries[index + 1];

      const changedFields = new Set(
        DIFFABLE_FIELDS.filter(
          ({ raw }) => !previousEntry || normalize(entry[raw]) !== normalize(previousEntry[raw]),
        ).map(({ column }) => column),
      );

      return {
        ...entry,
        subscriptionTypeLabel: getSubscriptionTypeLabel(entry.subscriptionType),
        subscriptionTermLabel: getSubscriptionTermLabel(entry.subscriptionTerm),
        subscriptionStartDateLabel: formatDateLabel(entry.subscriptionStartDate),
        subscriptionEndDateLabel: formatDateLabel(entry.subscriptionEndDate),
        historyDateLabel: entry.historyDate
          ? format(new Date(entry.historyDate), 'yyyy-MM-dd HH:mm')
          : '--',
        changedFields,
      };
    });
  }, [data]);

  return (
    <Box sx={{ position: 'relative' }}>
      <Typography variant="h6" color="#fff" mb={2}>
        History
      </Typography>
      <TableContainer
        roundedTop
        styles={{
          height: 'auto',
          maxHeight: 'calc(100vh - 334px)',
          overflowY: 'auto',
          overflowX: 'auto',
        }}>
        <Table stickyHeader aria-label="customer history table" sx={{ tableLayout: 'auto' }}>
          <TableHeading
            headings={headings}
            hasFilter={false}
            listCellStyles={{ top: '0', whiteSpace: 'normal' }}
          />
          <TableBody sx={{ backgroundColor: '#4B4B4B' }}>
            {isLoading ? (
              <LoadingRows columnCount={headings.length} rowCount={5} />
            ) : rows.length ? (
              <>
                {rows.map((row, index) => (
                  <TableRow
                    ref={index === rows.length - 1 ? sentryRef : null}
                    key={row.id}
                    hover
                    onClick={() => setSelectedRow(row)}
                    sx={{ cursor: 'pointer' }}>
                    {headings.map(heading => (
                      <GenericTableCell
                        key={heading.name}
                        value={row[heading.name]}
                        highlightText={false}
                        styles={getCellStyles(heading, row)}
                      />
                    ))}
                  </TableRow>
                ))}
                {isFetchingNextPage && (
                  <LoadingRows columnCount={headings.length} rowCount={3} />
                )}
              </>
            ) : (
              <NoDataCell colSpan={headings.length}>No history yet</NoDataCell>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <HistoryRowDialog open={!!selectedRow} onClose={() => setSelectedRow(null)} row={selectedRow} />
    </Box>
  );
};

export default CompanyHistory;
